import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Archive, Loader2 } from 'lucide-react';

const REASONS = [
  { key: 'seasonal_hold', label: 'Seasonal hold', hint: 'Snow / rain — revive it next season from the Archive tab' },
  { key: 'on_hold', label: 'On hold', hint: 'Waiting on the customer, parts or access' },
  { key: 'cancelled_by_customer', label: 'Cancelled by customer', hint: 'Customer withdrew the work after it was issued' },
  { key: 'reissued', label: 'Re-issued', hint: 'A new job replaces this one' },
  { key: 'completed_filing', label: 'Completed — filing', hint: 'Finished work, tidy it out of the active lists' },
  { key: 'other', label: 'Other', hint: 'Type your own reason' },
];

// Archive any job (to-do, in progress, paused, completed) with a reason.
// In-progress jobs have their open crew sessions closed and are stored as paused.
export const ArchiveJobDialog = ({ job, onClose, onConfirm, busy }) => {
  const [reason, setReason] = useState('seasonal_hold');
  const [other, setOther] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  if (!job) return null;
  const live = job.status === 'in_progress';
  const finalReason = reason === 'other' ? (other.trim() || 'other') : reason;
  const hasCustomer = !!(job.customer_email || job.customer_id);

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md bg-[#1A2433] border-[#2D3748] text-white" data-testid="archive-job-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="w-4 h-4 text-[#FFA000]" /> Archive job</DialogTitle>
          <DialogDescription className="text-slate-400">
            {job.job_number ? `${job.job_number} · ` : ''}{job.title} — currently <b className="text-slate-200">{String(job.status || '').replace('_', ' ')}</b>.
            Archived jobs leave the active lists but keep every hour, note and photo; restore them any time from the Archive tab.
          </DialogDescription>
        </DialogHeader>
        {live && (
          <p className="text-xs rounded-lg px-3 py-2" style={{ background: 'rgba(255,160,0,0.12)', color: '#FFA000' }} data-testid="archive-live-warning">
            This job is in progress — archiving stops the clock for everyone on it and stores it as paused.
          </p>
        )}
        <div className="space-y-1.5" data-testid="archive-reason-list">
          {REASONS.map((r) => (
            <label key={r.key} className="flex items-start gap-2 p-2 rounded-lg cursor-pointer hover:bg-[#0F1923]" style={{ border: `1px solid ${reason === r.key ? '#FFA000' : '#2D3748'}` }} data-testid={`archive-reason-${r.key}`}>
              <input type="radio" name="archive-reason" className="mt-0.5" checked={reason === r.key} onChange={() => setReason(r.key)} />
              <span><span className="text-sm">{r.label}</span><span className="block text-[11px] text-slate-500">{r.hint}</span></span>
            </label>
          ))}
          {reason === 'other' && (
            <input value={other} onChange={(e) => setOther(e.target.value)} placeholder="Reason" maxLength={200} className="w-full px-3 py-2 rounded-lg text-sm bg-[#0F1923] border border-[#2D3748] text-white" data-testid="archive-reason-other-input" />
          )}
          {reason === 'cancelled_by_customer' && (
            <label className="flex items-start gap-2 p-2 rounded-lg text-xs cursor-pointer" style={{ background: 'rgba(30,136,229,0.10)', border: '1px solid rgba(30,136,229,0.35)' }} data-testid="archive-notify-customer">
              <input type="checkbox" className="mt-0.5" checked={notifyCustomer && hasCustomer} disabled={!hasCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} data-testid="archive-notify-customer-checkbox" />
              <span>
                <span className="text-slate-200">Email the customer a cancellation confirmation</span>
                <span className="block text-slate-500">{hasCustomer ? `Sent to ${job.customer_email || 'the customer on file'} with the job reference and hours already worked.` : 'No customer email on this job — nothing will be sent.'}</span>
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="border-[#2D3748] text-slate-300" data-testid="archive-cancel-btn">Keep job</Button>
          <Button onClick={() => onConfirm(finalReason, reason === 'cancelled_by_customer' && hasCustomer && notifyCustomer)} disabled={busy} className="bg-[#FFA000] hover:bg-amber-600 text-black" data-testid="archive-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Archive className="w-4 h-4 mr-1" />} Archive job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ArchiveJobDialog;
